import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { CombinedActionSheet } from '@/components/combined-action-sheet';
import { createStyles } from '@/contexts/theme-context';
import { ItemListOptionMetadata } from '@/contexts/item-context-types';
import { OrderGuidePartitionList } from '@/components/order-guide-partition-list';
import {
  SampleRequestFeedbackType,
  SampleRequestVariantPackFieldsFragment,
} from '@/types';
import { SampleRequestFollowUpAddToOrderGuide } from './sample-request-follow-up-add-to-order-guide';
import { SampleRequestFollowUpFeedback } from './sample-request-follow-up-feedback';
import { SampleRequestFollowUpInitial } from './sample-request-follow-up-initial';
import { SampleRequestFollowUpStepHeader } from './sample-request-follow-up-step-header';
import { Text, useWindowDimensions } from 'react-native';
import { Type } from '@/components/toast';
import { TYPOGRAPHY } from '@/styles';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useOrderGuideItemsContext } from '@/contexts/order-guide-items-context';
import { useSubmitSampleRequestFollowUp } from '@/hooks/use-submit-sample-request-follow-up';
import { useToasts } from '@/contexts/toast-context';

enum FollowUpRoute {
  ADD_TO_ORDER_GUIDE = 'ADD_TO_ORDER_GUIDE',
  ADD_TO_ORDER_GUIDE_PROMPT = 'ADD_TO_ORDER_GUIDE_PROMPT',
  NOT_RECEIVED = 'NOT_RECEIVED',
  RECEIVING_CONFIRMATION = 'RECEIVING_CONFIRMATION',
  YOUR_EXPERIENCE = 'YOUR_EXPERIENCE',
}

interface Props {
  metadata: ItemListOptionMetadata;
  onClose: (showSuccessToast?: boolean) => void;
  sampleRequest: SampleRequestVariantPackFieldsFragment;
}

const MODAL_MAX_WIDTH = 452;

export const SampleRequestFollowUp: React.FC<Props> = ({
  metadata,
  onClose,
  sampleRequest,
}) => {
  const [styles] = useStyles();
  const { height } = useWindowDimensions();
  const maxHeight = Math.floor(height * 0.85);
  const { showErrorToast, showToast } = useToasts();
  const { submitSampleRequestFollowUp } = useSubmitSampleRequestFollowUp();
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useAuthenticatedUserContext();
  const { isInOrderGuide } = useOrderGuideItemsContext();

  const [feedbackType, setFeedbackType] = useState<
    SampleRequestFeedbackType | undefined
  >();
  const [feedbackText, setFeedbackText] = useState('');

  const [addedToOrderGuide, setAddedToOrderGuide] = useState(false);

  const { sample_request, variant_pack } = sampleRequest;
  const variantPackUUID = variant_pack?.uuid;
  if (!sample_request) {
    return null;
  }

  const sampleRequestUUID = sample_request.uuid;

  const commonVariables = {
    restaurantUUID,
    sampleRequestUUID,
    supplierUUID,
  };

  const onCloseFollowUp = async (activeRoute: FollowUpRoute) => {
    if (
      [
        FollowUpRoute.ADD_TO_ORDER_GUIDE,
        FollowUpRoute.ADD_TO_ORDER_GUIDE_PROMPT,
      ].includes(activeRoute)
    ) {
      await onSubmitFollowUp();
    } else if (activeRoute === FollowUpRoute.RECEIVING_CONFIRMATION) {
      try {
        await submitSampleRequestFollowUp({
          variables: commonVariables,
        });
      } catch {
        showErrorToast();
      }
      onClose();
    } else if (activeRoute === FollowUpRoute.YOUR_EXPERIENCE) {
      try {
        await submitSampleRequestFollowUp({
          variables: {
            ...commonVariables,
            followUpResponse: {
              sample_received: true,
            },
          },
        });
      } catch {
        showErrorToast();
      }
      onClose();
    } else {
      onClose();
    }
  };

  const onSubmitFollowUp = async () => {
    onClose();
    try {
      await submitSampleRequestFollowUp({
        variables: {
          ...commonVariables,
          followUpResponse: {
            added_to_order_guide: addedToOrderGuide,
            feedback_text: feedbackText,
            feedback_type: feedbackType,
            sample_received: true,
          },
        },
      });
      showToast({
        content: i18n.t('toast.follow_up_submitted'),
        type: Type.POSITIVE,
      });
    } catch {
      showErrorToast();
    }
  };

  return (
    <CombinedActionSheet
      contentStyle={{ maxHeight }}
      initialRoute={FollowUpRoute.RECEIVING_CONFIRMATION}
      modalStyle={styles.modal}
      onClose={onCloseFollowUp}
      renderScene={({ routeKey, jumpTo }) => {
        switch (routeKey) {
          case FollowUpRoute.RECEIVING_CONFIRMATION:
            return (
              <>
                <SampleRequestFollowUpStepHeader
                  onClose={() => onCloseFollowUp(routeKey)}
                  title={i18n.t('sample_request.did_you_receive_your_sample')}
                />
                <SampleRequestFollowUpInitial
                  onNotReceivedPress={async () => {
                    try {
                      await submitSampleRequestFollowUp({
                        variables: {
                          ...commonVariables,
                          followUpResponse: {
                            sample_received: false,
                          },
                        },
                      });
                      jumpTo(FollowUpRoute.NOT_RECEIVED);
                    } catch {
                      showErrorToast();
                      onClose();
                    }
                  }}
                  onReceivedPress={() => jumpTo(FollowUpRoute.YOUR_EXPERIENCE)}
                  photoURLs={metadata.photoURLs}
                  sampleRequest={sampleRequest}
                />
              </>
            );
          case FollowUpRoute.NOT_RECEIVED:
            return (
              <>
                <SampleRequestFollowUpStepHeader
                  onClose={() => onCloseFollowUp(routeKey)}
                  title={i18n.t('sample_request.we_are_sorry_no_sample')}
                />
                <Text style={TYPOGRAPHY.p}>
                  {i18n.t('supplier_has_been_notified')}
                </Text>
              </>
            );
          case FollowUpRoute.YOUR_EXPERIENCE:
            return (
              <>
                <SampleRequestFollowUpStepHeader
                  onClose={() => onCloseFollowUp(routeKey)}
                  title={i18n.t('how_was_your_experience')}
                />
                <SampleRequestFollowUpFeedback
                  feedbackText={feedbackText}
                  feedbackType={feedbackType}
                  onFeedbackTextChange={setFeedbackText}
                  onFeedbackTypeSelect={setFeedbackType}
                  onSubmit={async () => {
                    if (
                      feedbackType === SampleRequestFeedbackType.Negative ||
                      (variantPackUUID && isInOrderGuide(variantPackUUID))
                    ) {
                      await onSubmitFollowUp();
                    } else {
                      jumpTo(FollowUpRoute.ADD_TO_ORDER_GUIDE);
                    }
                  }}
                  sampleRequestUUID={sample_request.uuid}
                />
              </>
            );
          case FollowUpRoute.ADD_TO_ORDER_GUIDE:
            return (
              <>
                <SampleRequestFollowUpStepHeader
                  onClose={() => onCloseFollowUp(routeKey)}
                  title={i18n.t('add_to_order_guide_sample')}
                />
                <SampleRequestFollowUpAddToOrderGuide
                  metadata={metadata}
                  onAddToOrderGuidePress={() =>
                    jumpTo(FollowUpRoute.ADD_TO_ORDER_GUIDE_PROMPT)
                  }
                  onSkipPress={onSubmitFollowUp}
                  sampleRequest={sampleRequest}
                />
              </>
            );
          case FollowUpRoute.ADD_TO_ORDER_GUIDE_PROMPT:
            return variantPackUUID ? (
              <>
                <SampleRequestFollowUpStepHeader
                  isLastStep
                  onClose={() => onCloseFollowUp(routeKey)}
                  title={i18n.t('manage_order_guide_item')}
                />
                <OrderGuidePartitionList
                  onSelectionChangeCallBack={setAddedToOrderGuide}
                  variantPackUUID={variant_pack.uuid}
                />
              </>
            ) : null;
        }
      }}
      routes={{
        [FollowUpRoute.RECEIVING_CONFIRMATION]: {
          previousKey: null,
          showCloseButton: false,
        },
        [FollowUpRoute.NOT_RECEIVED]: {
          previousKey: null,
          showCloseButton: false,
        },
        [FollowUpRoute.YOUR_EXPERIENCE]: {
          previousKey: null,
          showCloseButton: false,
        },
        [FollowUpRoute.ADD_TO_ORDER_GUIDE]: {
          previousKey: null,
          showCloseButton: false,
        },
        [FollowUpRoute.ADD_TO_ORDER_GUIDE_PROMPT]: {
          previousKey: null,
          showCloseButton: false,
        },
      }}
    />
  );
};

const useStyles = createStyles(() => ({
  modal: {
    maxWidth: MODAL_MAX_WIDTH,
  },
}));
